import { clampScore, hoursBetween, type EvidenceRecord, type ExecutionException, type OperatingLoad, type OperationalConfidence } from "./types";

interface ConfidenceArgs {
  load: OperatingLoad;
  evidence: EvidenceRecord[];
  exceptions: ExecutionException[];
  now: string;
}

const SOURCE_BASELINE: Record<string, number> = {
  samsara: 0.95,
  motive: 0.93,
  geotab: 0.9,
  dat: 0.78,
  truckstop: 0.78,
  sendgrid: 0.72,
  twilio: 0.7,
  fmcsa: 0.82,
  dispatcher: 0.55,
};

export function computeOperationalConfidence(args: ConfidenceArgs): OperationalConfidence {
  const evidenceScore = evidenceReliability(args.evidence, args.now);
  const exceptionPenalty = args.exceptions.reduce((total, exception) => {
    if (exception.severity === "critical") return total + 35;
    if (exception.severity === "high") return total + 25;
    if (exception.severity === "medium") return total + 15;
    return total + 8;
  }, 0);

  const loadCompleteness = requiredFieldScore(args.load);
  const visibilityConfidence = clampScore(evidenceScore);
  const loadHealthScore = clampScore((loadCompleteness + visibilityConfidence) / 2 - exceptionPenalty);
  const exceptionRiskScore = clampScore(100 - loadHealthScore + exceptionPenalty / 2);

  return {
    loadHealthScore,
    visibilityConfidence,
    etaConfidence: clampScore(visibilityConfidence - (args.load.lifecycleState === "covered" ? 8 : 0)),
    exceptionRiskScore,
    fieldConfidence: {
      lifecycleState: 95,
      carrier: args.load.carrierName ? 85 : 25,
      pickupWindow: args.load.pickupWindowStart && args.load.pickupWindowEnd ? 90 : 20,
      deliveryWindow: args.load.deliveryWindowStart && args.load.deliveryWindowEnd ? 90 : 20,
    },
    computedFromEvidenceIds: args.evidence.map((item) => item.id),
  };
}

function evidenceReliability(evidence: EvidenceRecord[], now: string): number {
  if (evidence.length === 0) return 20;
  const scored = evidence.map((item) => {
    const ageHours = Math.max(0, hoursBetween(item.occurredAt, now));
    const recency = Math.max(0.15, 1 - ageHours / 24);
    const sourceReliability = SOURCE_BASELINE[item.source] ?? item.reliabilityScore;
    return 100 * Math.min(sourceReliability, item.reliabilityScore) * recency;
  });
  return scored.reduce((total, value) => total + value, 0) / scored.length;
}

function requiredFieldScore(load: OperatingLoad): number {
  const fields = [
    load.reference,
    load.customerName,
    load.carrierName,
    load.origin.city,
    load.origin.state,
    load.destination.city,
    load.destination.state,
    load.pickupWindowStart,
    load.pickupWindowEnd,
    load.deliveryWindowStart,
    load.deliveryWindowEnd,
    load.equipmentType,
  ];
  return (fields.filter(Boolean).length / fields.length) * 100;
}
